<template>
  <div class="forgot-password-container">
    <div class="forgot-password-card">
      <!-- Header -->
      <div class="form-header">
        <h1 class="form-title">Reset Password</h1>
        <p class="form-subtitle">
          {{ !otpSent ? 'Enter your phone number to receive an OTP' : 'Enter the OTP and your new password' }}
        </p>
      </div>

      <!-- Form -->
      <v-form ref="form" v-model="valid" lazy-validation class="reset-form">

        <!-- Phone Number Field -->
        <div class="form-group">
          <label class="form-label">Phone Number</label>
          <v-text-field
            v-model="phoneNumber"
            :rules="phoneRules"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            type="text"
            required
            :disabled="otpSent"
            class="form-field"
          ></v-text-field>
        </div>

        <!-- OTP and New Password Fields (shown after OTP is sent) -->
        <div v-if="otpSent" class="otp-section">
          <div class="form-group">
            <label class="form-label">OTP Code</label>
            <v-text-field
              v-model="otp"
              :rules="otpRules"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              type="text"
              required
              class="form-field"
            ></v-text-field>
          </div>

          <div class="form-group">
            <label class="form-label">New Password</label>
            <v-text-field
              v-model="newPassword"
              :rules="passwordRules"
              variant="outlined"
              density="comfortable"
              :type="showPassword ? 'text' : 'password'"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              hide-details="auto"
              required
              @click:append-inner="showPassword = !showPassword"
              class="form-field"
            ></v-text-field>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="form-actions">
          <v-btn
            v-if="!otpSent"
            block
            color="primary"
            size="large"
            @click="initiateReset"
            :disabled="!valid || loading"
            :loading="loading"
            class="action-button"
          >
            Send OTP
          </v-btn>

          <v-btn
            v-else
            block
            color="primary"
            size="large"
            @click="completeReset"
            :disabled="!valid || loading"
            :loading="loading"
            class="action-button"
          >
            Reset Password
          </v-btn>
        </div>

        <!-- Back to Login Link -->
        <div class="back-link">
          <v-btn
            variant="text"
            color="primary"
            size="small"
            @click="goBack"
            class="back-button"
          >
            ← Back to Login
          </v-btn>
        </div>

      </v-form>

      <!-- Alert Messages -->
      <div v-if="errorMessage" class="alert-section">
        <v-alert
          type="error"
          variant="tonal"
          closable
          density="compact"
          class="alert-message"
        >
          {{ errorMessage }}
        </v-alert>
      </div>

      <div v-if="successMessage" class="alert-section">
        <v-alert
          type="success"
          variant="tonal"
          closable
          density="compact"
          class="alert-message"
        >
          {{ successMessage }}
        </v-alert>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ForgotPasswordForm',
  data() {
    return {
      valid: true,
      phoneNumber: '',
      otp: '',
      newPassword: '',
      showPassword: false,
      otpSent: false,
      phoneRules: [
        v => !!v || 'Phone number is required',
        v => /^\d+$/.test(v) || 'Phone number must contain only digits'
      ],
      otpRules: [
        v => !!v || 'OTP is required',
        v => /^\d+$/.test(v) || 'OTP must contain only digits'
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 6 || 'Password must be at least 6 characters'
      ],
      loading: false,
      errorMessage: '',
      successMessage: ''
    };
  },
  methods: {
    async initiateReset() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        // Use the specified API endpoint for password reset initiation
        await axios.post(`${pettyCashApi}users/password-reset/initiate`, {
          phoneNumber: this.phoneNumber
        });

        this.successMessage = 'OTP sent to your phone. Please enter it along with your new password.';
        this.otpSent = true;
      } catch (error) {
        console.error('Error initiating password reset:', error);
        this.errorMessage = error.response?.data?.message || 'Failed to initiate password reset. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async completeReset() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        // Use the specified API endpoint for completing password reset
        await axios.post(`${pettyCashApi}users/password-reset/complete`, {
          phoneNumber: this.phoneNumber,
          otp: this.otp,
          newPassword: this.newPassword
        });

        this.successMessage = 'Password reset successful. You can now login with your new password.';

        // Reset form
        this.phoneNumber = '';
        this.otp = '';
        this.newPassword = '';
        this.otpSent = false;

        // Redirect to login after a short delay
        setTimeout(() => {
          this.$router.push({ name: 'LoginForm' });
        }, 3000);
      } catch (error) {
        console.error('Error completing password reset:', error);
        this.errorMessage = error.response?.data?.message || 'Failed to complete password reset. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'LoginForm' });
    }
  }
};
</script>

<style scoped lang="scss">
.forgot-password-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: rgb(var(--v-theme-background));
  padding: 20px;
}

.forgot-password-card {
  background: rgb(var(--v-theme-surface));
  border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-border-color));
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-size: 28px;
  font-weight: 600;
  color: rgb(var(--v-theme-text-primary));
  margin: 0 0 8px 0;
}

.form-subtitle {
  font-size: 14px;
  color: rgb(var(--v-theme-text-secondary));
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
}

.reset-form {
  .form-group {
    margin-bottom: 20px;
  }

  .form-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: rgb(var(--v-theme-text-primary));
    margin-bottom: 6px;
  }

  .form-field {
    :deep(.v-field) {
      border-radius: 8px;
      border: 1px solid rgb(var(--v-theme-border-color));
      background-color: rgb(var(--v-theme-surface));

      &:hover:not(.v-field--focused) {
        border-color: rgba(var(--v-theme-on-surface), 0.5);
      }

      &.v-field--focused {
        border-color: rgb(var(--v-theme-primary));
        box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.1);
      }

      &.v-field--error {
        border-color: rgb(var(--v-theme-error));
      }

      &.v-field--disabled {
        background-color: rgba(var(--v-theme-on-surface), 0.05);
        border-color: rgb(var(--v-theme-border-color));
        opacity: 0.7;
      }
    }

    :deep(.v-field__input) {
      font-size: 14px;
      padding: 12px 16px;
      min-height: 48px;
    }

    :deep(.v-field__append-inner) {
      padding-top: 0;
      align-items: center;

      .v-icon {
        color: rgb(var(--v-theme-text-secondary));
        font-size: 18px;
        cursor: pointer;

        &:hover {
          color: rgb(var(--v-theme-text-primary));
        }
      }
    }
  }

  .otp-section {
    border-top: 1px solid rgb(var(--v-theme-border-color));
    padding-top: 24px;
    margin-top: 24px;
  }

  .form-actions {
    margin-top: 32px;
    margin-bottom: 20px;
  }

  .action-button {
    height: 48px;
    font-size: 14px;
    font-weight: 500;
    text-transform: none;
    border-radius: 8px;
    box-shadow: none;

    &:hover {
      box-shadow: 0 2px 4px rgba(var(--v-theme-primary), 0.2);
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  .back-link {
    text-align: center;
  }

  .back-button {
    font-size: 13px;
    font-weight: 500;
    text-transform: none;
    color: rgb(var(--v-theme-primary));
    padding: 8px 16px;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }
  }
}

.alert-section {
  margin-top: 20px;

  .alert-message {
    border-radius: 8px;
    font-size: 13px;
  }
}

// Responsive adjustments
@media (max-width: 600px) {
  .forgot-password-container {
    padding: 16px;
  }

  .forgot-password-card {
    padding: 32px 24px;
    max-width: 100%;
  }

  .form-title {
    font-size: 24px;
  }
}

// Remove Vuetify's default field styling that might interfere
:deep(.v-messages) {
  font-size: 12px;
  color: rgb(var(--v-theme-error));
  margin-top: 4px;
}

:deep(.v-field--variant-outlined .v-field__outline) {
  --v-field-border-width: 1px;
  --v-field-border-opacity: 1;
}
</style>
